import { useMemo } from 'react';
import { useBooking } from '../../hooks/useBooking';
import { useSiteColors } from '../../hooks/useSiteColors';
import {
  getSocialSimpleIcon,
  normalizeListingSocials,
  socialProfileUrl,
  sortListingSocials,
} from '../../utils/socials';

export function AboutSection() {
  const { businessListing } = useBooking();
  const c = useSiteColors();

  const galleryImages = businessListing?.cover_images ?? [];

  const aboutSocialEntries = useMemo(() => {
    const list = normalizeListingSocials(businessListing?.socials);
    if (!list?.length) return [];
    return sortListingSocials(list)
      .map((s, i) => {
        const icon = getSocialSimpleIcon(s.platform);
        const href = socialProfileUrl(s.platform, s.value ?? '');
        if (!icon || !href) return null;
        return { key: `${s.platform}-${i}`, icon, href, title: icon.title };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  }, [businessListing?.socials]);

  const showAboutSection =
    Boolean(businessListing?.about?.trim()) ||
    galleryImages.length > 0 ||
    aboutSocialEntries.length > 0;

  if (!businessListing || !showAboutSection) return null;

  const hasPhotoGallery = galleryImages.length > 0;

  return (
    <section
      id="about"
      className="py-20"
      style={{ borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}` }}
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className={`grid grid-cols-1 ${hasPhotoGallery ? 'lg:grid-cols-2 gap-16' : ''} items-start`}>
          {hasPhotoGallery && (
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-3">
                {galleryImages.map((src, i) => (
                  <div key={`${src}-${i}`} className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                    <img
                      src={src}
                      alt={`${businessListing.business_name} gallery ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="order-1 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase mb-4" style={{ color: c.link }}>
              About Us
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-6" style={{ color: c.text }}>
              {businessListing.business_name}
            </h2>
            {businessListing.about ? (
              <div className="space-y-4">
                {businessListing.about.split('\n').filter(Boolean).map((paragraph, i) => (
                  <p key={i} className="text-[15px] leading-[1.85]" style={{ color: c.mutedText }}>
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : hasPhotoGallery ? (
              <p className="text-[15px] leading-[1.85]" style={{ color: c.mutedText }}>
                Browse our gallery for a closer look at what we offer.
              </p>
            ) : (
              <p className="text-[15px] leading-[1.85]" style={{ color: c.mutedText }}>
                Connect with us on social media below.
              </p>
            )}
          </div>
        </div>

        {aboutSocialEntries.length > 0 && (
          <div
            className="mt-14 flex flex-wrap items-center justify-center lg:justify-start gap-3"
            role="list"
            aria-label="Social media profiles"
          >
            {aboutSocialEntries.map(({ key, icon, href, title }) => (
              <a
                key={key}
                role="listitem"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${title} (opens in a new tab)`}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ backgroundColor: c.background, borderColor: c.border, outlineColor: c.link }}
              >
                {/* One color, so dark brand marks stay visible on dark backgrounds */}
                <svg role="presentation" viewBox="0 0 24 24" width={22} height={22} className="shrink-0" aria-hidden>
                  <path fill={c.text} d={icon.path} />
                </svg>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
